import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { MEAPIService } from 'src/app/common/services/api/me-api.service';
import { MatDialog } from '@angular/material/dialog';
import { ChatMessage, ChatMessageDisplay } from './chat-message';
import { InternalSettingsService } from 'src/app/common/services/internal-settings/internal-settings.service';
import { ChatStatus, HelpdeskEventStatus, HelpDeskTicket } from 'src/app/business/help-desk/help-desk-ticket';
import { Overlay } from '@angular/cdk/overlay';
import { ResponsiveDesignService } from '../../services/responsive-design/responsive-design.service';
import { ChatUtility } from './chat-utility';
import { logger } from 'src/app/common/services/logging/logging.service';
import { WebSocketService } from 'src/app/system/utility/websocket.service';
import { SocketChannel } from '../../services/message-handler/socket-message.bean';
import { AbstractChatService } from './abstract-chat.service';
import { TranslateUtilityService } from 'src/app/system/utility/translation-utility.service';
import { ChatContextService } from './chat-context.service';
import { PlatformService } from 'src/app/system/utility/platform.service';
import { StringUtilityService } from 'src/app/system/utility/string-utility.service';
import { CameraService } from 'src/app/system/utility/camera.service';
import { Photo } from '@capacitor/camera';

@Injectable({
  providedIn: 'root'
})
export class ChatService extends AbstractChatService {

  public currentState: ChatDialogState = ChatDialogState.CLOSE;
  public hasNewActivity: Subject<boolean> = new Subject<boolean>();

  private readonly toBeSentAsRead: { messageId: string, senderId: string, localId: string }[] = [];

  constructor(
    srvApi: MEAPIService,
    translate: TranslateUtilityService,
    srvIs: InternalSettingsService,
    srvChatContext: ChatContextService,
    srvWebSocket: WebSocketService,
    srvDialog: MatDialog,
    overlay: Overlay,
    srvRpDesign: ResponsiveDesignService,
    private readonly srvCamera: CameraService
  ) {

    super(srvApi, translate, srvIs, srvChatContext, srvWebSocket, srvDialog, overlay, srvRpDesign);
  }

  /**
   * Public Method to allow state change from Chat Component
   */
  public changeState(newState: ChatDialogState, options: any = {}) {
    switch (newState) {
      case ChatDialogState.OUTSIDE_OF_HOUR:
        this.srvChatContext.setOOHContext(options);
        break;
      case ChatDialogState.ACTIVE:
        if (!StringUtilityService.isEmpty(options)) this.startChat(options);
        break;
      default:
        break;
    }
    this.stateDialog$.next(newState);
  }

  /**
   * Handle Dialog State Change
   */
  protected override handleStateChange(newState: ChatDialogState) {

    // Discard the 
    if (!PlatformService.isBrowser()) return;

    logger.debug('Changing state from: ' + this.currentState + ' to ' + newState);

    this.hasNewActivity.next(false);
    let ticket = this.srvChatContext.getChatTicket();

    switch (newState) {
      case ChatDialogState.CLOSE:
        if (this.dlgChat) this.dlgChat.close();
        this.srvChatContext.setChatTicket(null);
        break;

      case ChatDialogState.MINIMIZED:
        if (ticket) {
          this.connect();
          this.reOpenDialog();
        }
        break;

      case ChatDialogState.CREATE:
        this.connect();
        this.reOpenDialog();
        break;

      case ChatDialogState.ACTIVE:
        this.connect();

        if (ticket && this.srvChatContext.getDisplayMessages().length == 0) {
          // Get Chat History
          this.loadCurrentChatHistory(ticket).subscribe((r) => {
            this.reOpenDialog();
            this.sendReadMessages();
          })

        } else {
          // Chat Room Entry Point is here
          this.reOpenDialog();
          this.sendReadMessages();
        }
        break;

      case ChatDialogState.END_CHAT:
        this.connect();
        break;

      case ChatDialogState.CLOSED_BY_EMPLOYEE:
        this.connect();
        break;

      case ChatDialogState.OUTSIDE_OF_HOUR:
        this.reOpenDialog();
        break;
    }

    // Update State
    this.currentState = newState;
  }

  /**
   * Send Acknowledge (Read) message back to teller
   */
  private sendReadMessages() {

    let ticket = this.srvChatContext.getChatTicket();
    if (!ticket) {
      logger.error(`Ticket cannot be null`);
      return;
    }

    while (this.toBeSentAsRead.length > 0) {
      let message = this.toBeSentAsRead.shift();
      if (!message) continue;

      let msgRead: ChatMessage = {
        channel: "hd",
        pm: message.senderId,
        messageId: message.messageId,
        localId: message.localId,
        helpDeskTicketId: ticket.id,
        read: true
      }
      this.sendSocketMessage(ChatUtility.serializeSocketMessage(msgRead));
    }
  }

  public createChat() {
    this.srvChatContext.setChatTicket(null);
    this.changeState(ChatDialogState.CREATE);
  }

  private startChat(options: { ticket: HelpDeskTicket, userInputMessage: string }) {
    let ticket = options.ticket;
    let userInputMessage = options.userInputMessage;

    this.srvChatContext.setChatTicket(ticket);

    this.srvChatContext.resetDisplaymessage();
    this.srvChatContext.pushDisplayMessage(ChatUtility.buildCreateMessage(this.srvChatContext, userInputMessage));
    if (ticket.queuePosition > 0) {
      let msgQueue: ChatMessageDisplay = ChatUtility.buildQueueMessage(this.srvChatContext, ticket);
      if (msgQueue) this.srvChatContext.pushDisplayMessage(msgQueue);
    }
  }

  /**
   * This is the entry point when incoming message is received
   * 1) From the this.wsSubject object (Browser Mode)
   * 2) From the SocketMessageDispatcher (Mobile Mode)
   */
  override handleSocketMessage(rawMsg: ChatMessage | string) {

    // Parse the Socket Message if it is string
    let msg: ChatMessage = (typeof rawMsg == 'string') ? ChatUtility.parseSocketMessage(rawMsg) : rawMsg;

    let ticket = this.srvChatContext.getChatTicket();
    if (!ticket) return;

    // The websocket sends the create message as part of the create API call
    // so we capture it to let the user know that it has been delivered.
    // This means that we don't have a ticket yet (as it it still being returned from the server)
    if ((ticket.status == HelpdeskEventStatus.QUEUE) && (msg.helpDeskEvent == HelpdeskEventStatus.QUEUE)) {
      if (msg.delivered) {
        let message = this.srvChatContext.getDisplayMessages()[0];
        message.hasDelivered = msg.delivered;
      }
    }

    // If the channel is con - the other person has read the message. 
    if (msg.channel == SocketChannel.CONFIRMATION) {
      this.handleConfirmationMessage(msg);
      return;
    }

    if (msg.channel == SocketChannel.HELPDESK) {
      this.handleHelpdeskMessage(msg);
      return;
    }

    logger.info(`Received message without Channel from ${msg.senderName} (${msg.senderId})`);

    // Channel is empty
    this.handleHelpdeskMessage(msg);
  }

  private handleConfirmationMessage(msg: ChatMessage) {
    this.updateDisplayMessages(msg, true);
  }

  private handleHelpdeskMessage(msg: ChatMessage) {

    let ticket = this.srvChatContext.getChatTicket();
    if (!ticket) {
      logger.error(`Unknown status for ticket ${msg.helpDeskTicketId}`);
      return;
    }

    if (msg.helpDeskTicketId == ticket.id) {

      // Set the Employeed Id
      this.srvChatContext.setEmployeeId(msg);

      switch (msg.helpDeskEvent) {
        // -1 is the default if no helpDeskEvent is sent - this means that the message was a chat exchange.
        case HelpdeskEventStatus.UNKNOWN:

          // if there isn't a senderId, then this was sent from this customer 
          // and we need to set it to get the display to work correctly.
          if (!msg.senderId) {
            msg.senderId = this.srvChatContext.userName;
            msg.senderName = this.srvChatContext.displayName;
          }
          if (msg.qp) {
            msg.messageId = '-2';
          } else {

            let message = {
              messageId: msg.messageId!,
              senderId: msg.senderId,
              localId: msg.localId ?? ''
            }
            this.toBeSentAsRead.push(message);

            if (this.currentState != ChatDialogState.MINIMIZED) {
              this.sendReadMessages()
            }
          }
          this.updateDisplayMessages(msg);
          break;

        // 0 is the create message - this is sent when the chat is created.
        case HelpdeskEventStatus.QUEUE:
          break;

        // 1 is the assigned message - this is sent when the chat is assigned to an employee.
        case HelpdeskEventStatus.ACTIVE: {

          // Update the overall ticket to be Deliver (no use at all)
          ticket.status = ChatStatus.DELIVERED; // or Read ?

          // Replace the temporary ticket with the real one from the server. 
          let msgTime: Date = new Date(msg.timestamp!);
          let dateDisplay = ChatUtility.getDateDisplay(msgTime)

          let displayMessages = this.srvChatContext.getDisplayMessages();
          // Update First message to be acknowledge
          displayMessages[0].dateDisplay = ChatUtility.getDateDisplay(msgTime);
          displayMessages[0].hasRead = true;
          displayMessages[0].hasDelivered = true;
          displayMessages[0].hasSent = true;

          // Update the second message to be the assigned message
          displayMessages[1].inQueue = false;

          // If the message is come from websocket, then we need to update the display message
          if (msg.channel == SocketChannel.HELPDESK) {
            let msgJoined: ChatMessageDisplay = ChatUtility.buildJoinedChatMessage(this.srvChatContext, ticket, msg, { dateDisplay });
            this.srvChatContext.pushDisplayMessage(msgJoined);
            this.hasNewActivity.next(true);
          }

          break;
        }
        case HelpdeskEventStatus.CLOSED_BY_CUSTOMER: {//2
          // The user has closed it in another channel
          let content = this.translate.instant('CHAT.YOU_HAVE_ENDED_THE_TICKET');
          let endMessage: ChatMessageDisplay = ChatUtility.buildEndChatMessage(this.srvChatContext, ticket, content);
          this.srvChatContext.pushDisplayMessage(endMessage);
          this.changeState(ChatDialogState.CLOSE);

          break;
        }
        case HelpdeskEventStatus.CLOSED_BY_EMPLOYEE: { //3
          // Need further screen for mobile app 
          // this.setChatTicket(null);
          // this.changeState(ChatDialogState.CLOSE);
          let content = this.translate.instant('CHAT.EMPLOYEE_HAVE_ENDED_THE_TICKET', { senderName: msg.senderName });
          let endMessage: ChatMessageDisplay = ChatUtility.buildEndChatMessage(this.srvChatContext, ticket, content);
          this.srvChatContext.pushDisplayMessage(endMessage);
          this.changeState(ChatDialogState.CLOSED_BY_EMPLOYEE);
          break;
        }

        case HelpdeskEventStatus.EXPIRED:             //4
          this.changeState(ChatDialogState.END_CHAT);
          break;
      }
    }
  }

  /**
   * For sending attachment to the server via HTTP POST
   */
  public sendAttachment(file: File, filename: string, resize : boolean = false) {

    let ticket = this.srvChatContext.getChatTicket();
    if (ticket == null) {
      logger.error(`Ticket cannot be null`);
      return;
    }

    let helpDeskTicketId = ticket.id;

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onloadend = () => {
      if (!reader.result) return;

      let base64Data: string = reader.result as string;
      if (resize) {
        let photo : Photo = {
          base64String: base64Data,
          format: file.type,
          saved: false
        }
        this.srvCamera.resizePhotoByDimensions(photo, 640, 480).then((value) => {
          if (!value.dataUrl) {
            throw new Error("Resize Photo did not work");
          }
          this.handleAttachment(value.dataUrl, filename, false, helpDeskTicketId);
        })
      } else {
        this.handleAttachment(base64Data, filename, (file.type == 'application/pdf'), helpDeskTicketId);
      }

    }
  }

  private handleAttachment(base64Data : string, filename: string, isPdf: boolean, helpDeskTicketId: number) {
      let localId = crypto.randomUUID();

      let imageMsg: ChatMessage = {
        imageData: base64Data,
        imageName: filename,
        isPdf: isPdf,
        helpDeskTicketId: helpDeskTicketId,
        fromServer: true,                       // ?? From Server ?
        pm: this.srvChatContext.getEmployeeId(),
        timestamp: Date.now(),
        senderId: this.srvChatContext.userName,
        senderName: this.srvChatContext.displayName,
        localId: localId
      }
      let chatDisplayMessage = ChatUtility.createChatMessageDisplay(this.srvChatContext, imageMsg);
      this.srvChatContext.pushDisplayMessage(chatDisplayMessage);

      // Send to the server
      this.postChatAttachment(imageMsg);
  }

  /**
   * For sending Chat Message via WebSocket
   */
  public sendChatMessage(content: string) {

    let ticket = this.srvChatContext.getChatTicket();
    if (ticket == null) {
      logger.error(`Ticket cannot be null`);
      return;
    }

    this.connect();

    let chatMsg: ChatMessage = {
      channel: "hd",
      fromServer: true,
      pm: this.srvChatContext.getEmployeeId(),
      helpDeskTicketId: ticket.id,
      content: content,
      senderId: this.srvChatContext.userName,
      senderName: this.srvChatContext.displayName,
      timestamp: new Date().getTime(),
      localId: crypto.randomUUID()
    }

    // Update Display Message
    let chatDisplayMessage = ChatUtility.createChatMessageDisplay(this.srvChatContext, chatMsg);
    this.srvChatContext.pushDisplayMessage(chatDisplayMessage);

    // Send the Socket Message
    this.sendSocketMessage(ChatUtility.serializeSocketMessage(chatMsg));
  }

  private updateDisplayMessages(msgChat: ChatMessage, setRead: boolean = false) {

    let displayMessages = this.srvChatContext.getDisplayMessages();

    // find out if I have the message in the display messages
    let affected = displayMessages.find((m: ChatMessageDisplay) => {
      return ((!m.isLocalOnly) || (msgChat.messageId == '-2')) && (msgChat.messageId) && (+msgChat.messageId == m.messageId);
    });

    // This might be a message that the customer has sent and is wondering
    // if it has been delivered yet. Messages only get IDs when the server
    // has got them. 
    let unsent = displayMessages.filter((m: ChatMessageDisplay) => { return Number.isNaN(m.messageId) })

    if (!affected && (unsent.length > 0) && (msgChat.content || msgChat.imageData)) {
      affected = unsent.find((m: ChatMessageDisplay) => {
        return (!m.isLocalOnly) &&
          (((msgChat.content == m.content && (msgChat.content))) || (msgChat.localId == m.localId) && (msgChat.localId != ''))
      })
    }

    if (!affected && (msgChat.content || msgChat.imageData)) {
      if (this.currentState == ChatDialogState.MINIMIZED) {
        this.hasNewActivity.next(true);
      }

      let message = ChatUtility.createChatMessageDisplay(this.srvChatContext, msgChat);
      this.srvChatContext.pushDisplayMessage(message);
      return;
    }

    if (affected) {
      if (this.currentState == ChatDialogState.MINIMIZED) {
        this.hasNewActivity.next(true);
      }

      if (!affected.messageId) {
        affected.messageId = +msgChat.messageId!;
      }

      if (msgChat.qp) {
        affected.inQueue = true;
        affected.content = this.instant('CHAT.QUEUE_MESSAGE', { position: msgChat.qp });
      }

      if ((msgChat.delivered !== undefined) && (msgChat.delivered)) {
        affected.hasDelivered = true;
      }

      // Read messages are not stored in history, they are sent via CON messages which means we
      // have to determine whether the message is read based on context. 
      if (setRead) {
        affected.hasRead = true;
      }
    }
  }

  public createChatDialog() {
    if (this.currentState == ChatDialogState.CLOSE) {
      this.changeState(ChatDialogState.MINIMIZED);
    }
  }

}

export enum ChatDialogState {
  CLOSE = "CLOSE",
  CLOSED_BY_EMPLOYEE = "CLOSED_BY_EMPLOYEE",
  MINIMIZED = "MINIMIZED",
  ACTIVE = "ACTIVE",
  CREATE = "CREATE",
  END_CHAT = "END_CHAT",
  OUTSIDE_OF_HOUR = "OUTSIDE_OF_HOUR"
}